import React, { memo, useCallback, useMemo } from "react";
import { StyleSheet, View } from "react-native";
import { Calendar } from "react-native-calendars";
import Text from "@components/CustomText";
import color from "@constants/color";
import {
  canGoNextMonth,
  canGoPrevMonth,
  formatMonthLabel,
  getHistoryDayStyle,
} from "../utils";
import type { GigsHistoryDay, HistoryDayState } from "../types";

interface CalendarDayDate {
  dateString: string;
  day: number;
  month: number;
  year: number;
  timestamp: number;
}

interface CalendarDayProps {
  date?: CalendarDayDate;
  state?: "disabled" | "today" | "";
}

interface HistoryCalendarProps {
  month: string; // "YYYY-MM"
  minMonth: string;
  maxMonth: string;
  days: GigsHistoryDay[];
  onPrevMonth: () => void;
  onNextMonth: () => void;
}

/**
 * Month grid for Screen 4. Reuses `react-native-calendars`' `Calendar` with a
 * fully custom `dayComponent` (precedent: `src/screens/LedgerTxns/DateFilterModal.tsx`),
 * looked up by dateString rather than fighting the library's own
 * `markedDates`/`MarkingProps` typing.
 */
const HistoryCalendar: React.FC<HistoryCalendarProps> = ({
  month,
  minMonth,
  maxMonth,
  days,
  onPrevMonth,
  onNextMonth,
}) => {
  const dayStateByDate = useMemo(() => {
    const map: Record<string, HistoryDayState> = {};
    days.forEach((day) => {
      map[day.date] = day.state;
    });
    return map;
  }, [days]);

  // Memoized so the calendar doesn't recreate ~30-42 cell closures every
  // render — only when the month's data actually changes.
  const renderDay = useCallback(
    ({ date }: CalendarDayProps) => {
      if (!date) return <View style={styles.dayCell} />;
      const state = dayStateByDate[date.dateString] ?? "NOT_MARKED";
      const cellStyle = getHistoryDayStyle(state);
      // Built outside the JSX: a cell's colours depend on its state, so they
      // can't live in the StyleSheet.
      const stateStyle = {
        backgroundColor: cellStyle.backgroundColor,
        borderStyle: cellStyle.dashed
          ? ("dashed" as const)
          : ("solid" as const),
        borderWidth: cellStyle.dashed ? 1.5 : 0,
        borderColor: cellStyle.borderColor ?? "transparent",
      };
      return (
        <View
          style={[styles.dayCell, stateStyle]}
          testID={`gigs-history-day-${date.dateString}`}
        >
          <Text variant="body_text_3" style={{ color: cellStyle.textColor }}>
            {date.day}
          </Text>
        </View>
      );
    },
    [dayStateByDate],
  );

  const handlePressArrowLeft = useCallback(() => {
    if (canGoPrevMonth(month, minMonth)) onPrevMonth();
  }, [month, minMonth, onPrevMonth]);

  const handlePressArrowRight = useCallback(() => {
    if (canGoNextMonth(month, maxMonth)) onNextMonth();
  }, [month, maxMonth, onNextMonth]);

  const renderHeader = useCallback(
    () => (
      <Text variant="title_18" style={styles.monthLabel}>
        {formatMonthLabel(month)}
      </Text>
    ),
    [month],
  );

  return (
    <Calendar
      current={`${month}-01`}
      hideExtraDays
      disableMonthChange
      disableArrowLeft={!canGoPrevMonth(month, minMonth)}
      disableArrowRight={!canGoNextMonth(month, maxMonth)}
      onPressArrowLeft={handlePressArrowLeft}
      onPressArrowRight={handlePressArrowRight}
      renderHeader={renderHeader}
      dayComponent={renderDay}
      style={styles.calendar}
      theme={calendarTheme}
    />
  );
};

export default memo(HistoryCalendar);

const calendarTheme = {
  "stylesheet.calendar.header": {
    header: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingHorizontal: 4,
    },
    week: {
      marginTop: 8,
      flexDirection: "row",
      justifyContent: "space-around",
    },
  },
};

const styles = StyleSheet.create({
  calendar: {
    borderRadius: 12,
    paddingBottom: 8,
  },
  monthLabel: {
    color: color.mediumBlack,
  },
  dayCell: {
    width: 32,
    height: 32,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
});
